import { useEffect } from 'react';
import { collection, doc, getDoc, setDoc, query, where, getDocs, writeBatch, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';

export const useAutoCleanup = (userRole: string) => {
  useEffect(() => {
    // APAGADO POR SEGURIDAD: Evitamos que se borre o archive cualquier turno.
    // Cambiar a 'true' cuando estemos 100% seguros de querer limpiar la base de datos.
    const ENABLE_CLEANUP = false;
    
    if (!ENABLE_CLEANUP) return;

    // Solo permitimos que los administradores o co-administradores corran el proceso de limpieza
    if (userRole !== 'admin' && userRole !== 'superadmin' && userRole !== 'co_admin') return;

    const runCleanup = async () => {
      try {
        const configRef = doc(db, 'system_config', 'maintenance');
        const configSnap = await getDoc(configRef);
        
        const now = new Date();
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        
        if (configSnap.exists()) {
          const lastCleanup = configSnap.data().lastCleanupDate?.toDate();
          if (lastCleanup && lastCleanup > sevenDaysAgo) {
            // Ya se limpió en los últimos 7 días. Salimos en silencio.
            return;
          }
        }

        console.log('Iniciando mantenimiento automatizado de fondo...');
        
        const todayStr = now.toISOString().split('T')[0]; // YYYY-MM-DD
        const ninetyDaysAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        const ninetyDaysStr = ninetyDaysAgo.toISOString().split('T')[0];

        const bookingsRef = collection(db, 'bookings');
        
        // Pedimos TODOS los turnos con fecha anterior a hoy (usando índice simple automático)
        const qPastBookings = query(bookingsRef, where('date', '<', todayStr));
        const pastBookingsSnap = await getDocs(qPastBookings);
        
        let batch = writeBatch(db);
        let count = 0;

        const commitBatch = async () => {
          if (count > 0) {
            await batch.commit();
            batch = writeBatch(db);
            count = 0;
          }
        };

        for (const docSnap of pastBookingsSnap.docs) {
          const data = docSnap.data();
          const isOlderThan90Days = data.date < ninetyDaysStr;
          
          const isTrash = data.status === 'available' || data.status === 'cancelled';
          const isFinancial = data.status === 'paid' || data.status === 'confirmed' || data.courtId === 'expense';

          // EVITAR ELIMINAR O ARCHIVAR LAS RESERVAS FIJAS ACTIVAS
          // (Su 'date' puede ser de hace meses, pero siguen proyectándose hacia el futuro)
          if (data.isFixed && data.status !== 'cancelled') {
            continue;
          }

          if (isTrash) {
            // TAREA A: Eliminar Reservas Basura del pasado (Destructivo)
            batch.delete(docSnap.ref);
            count++;
          } else if (isFinancial && isOlderThan90Days) {
            // TAREA B: Archivar Turnos Financieros de más de 90 días (No Destructivo)
            const archivedRef = doc(db, 'archived_bookings', docSnap.id);
            batch.set(archivedRef, data); // Copiamos a la nueva colección
            count++;
            if (count >= 400) await commitBatch();
            
            batch.delete(docSnap.ref); // Borramos de la colección principal
            count++;
          }
          
          if (count >= 400) await commitBatch();
        }
        
        // --- TAREA C: Limpieza de Notificaciones Leídas y Antiguas ---
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        const notifsRef = collection(db, 'notifications');
        const qNotifs = query(notifsRef, where('read', '==', true), where('createdAt', '<', Timestamp.fromDate(thirtyDaysAgo)));
        
        try {
          // Si el query de notificaciones falla por falta de índice compuesto, hacemos fallback
          const notifsSnap = await getDocs(qNotifs);
          for (const docSnap of notifsSnap.docs) {
            batch.delete(docSnap.ref);
            count++;
            if (count >= 400) await commitBatch();
          }
        } catch (err) {
          console.warn('Requiere índice compuesto para notificaciones, omitiendo por ahora...', err);
        }

        // Commit final de todo lo que haya quedado
        await commitBatch();

        // Actualizamos la fecha de limpieza
        await setDoc(configRef, { lastCleanupDate: Timestamp.fromDate(now) }, { merge: true });
        console.log('Mantenimiento automatizado finalizado con éxito.');

      } catch (error) {
        console.error('Error en mantenimiento automático:', error);
      }
    };

    // Le damos unos segundos antes de arrancar para no trabar el renderizado inicial de la app
    const timer = setTimeout(() => {
      runCleanup();
    }, 5000);

    return () => clearTimeout(timer);
  }, [userRole]);
};
